import { useQuery } from 'react-query';

import { Transcript, TranscriptsResponse, TranscriptSubfact, TranscriptWithCount } from '../types/backend-dto-types';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig from '@/api/useGetFetchConfig';

export const useGetTranscripts = (caseId: string | null, page: number = 1, pageSize: number = 10) => {
  const { fetchConfigGET } = useGetFetchConfig();

  return useQuery<TranscriptsResponse>(
    [QueryEntity.Transcripts, { caseId, page, pageSize }],
    async () => {
      const res = await fetch(
        `${APIBaseChronos}/api/case/${caseId}/transcripts?page=${page}&pageSize=${pageSize}`,
        fetchConfigGET,
      );

      if (!res.ok) {
        throw new Error(`Failed to fetch transcripts: ${res.status} ${res.statusText}`);
      }

      return await res.json();
    },
    { refetchOnWindowFocus: false, enabled: !!caseId },
  );
};

export const useGetAllTranscripts = (caseId: string | null) => {
  const { fetchConfigGET } = useGetFetchConfig();

  return useQuery<TranscriptWithCount[]>(
    [QueryEntity.Transcripts, { caseId }],
    async () => {
      const res = await fetch(`${APIBaseChronos}/api/case/${caseId}/transcripts/all`, fetchConfigGET);

      if (!res.ok) {
        throw new Error(`Failed to fetch transcripts: ${res.status} ${res.statusText}`);
      }

      return await res.json();
    },
    { refetchOnWindowFocus: false, enabled: !!caseId },
  );
};

export const useGetTranscript = (transcriptId: string | null, caseId: string | null) => {
  const { fetchConfigGET } = useGetFetchConfig();

  return useQuery<Transcript>(
    [QueryEntity.Transcript, transcriptId, caseId],
    async () => {
      const res = await fetch(`${APIBaseChronos}/api/case/${caseId}/transcripts/${transcriptId}`, fetchConfigGET);

      if (!res.ok) {
        throw new Error(`Failed to fetch transcript: ${res.status} ${res.statusText}`);
      }

      return await res.json();
    },
    { refetchOnWindowFocus: false, enabled: !!transcriptId && !!caseId },
  );
};

export const useGetTranscriptSubfact = (
  caseId: string | null,
  transcriptId: string | null,
  subfactId: string | null,
) => {
  const { fetchConfigGET } = useGetFetchConfig();

  return useQuery<TranscriptSubfact>(
    [QueryEntity.TranscriptSubfact, caseId, subfactId],
    async () => {
      const res = await fetch(
        `${APIBaseChronos}/api/case/${caseId}/transcripts/${transcriptId}/subfacts/${subfactId}`,
        fetchConfigGET,
      );

      if (!res.ok) {
        throw new Error(`Failed to fetch transcript subfact: ${res.status} ${res.statusText}`);
      }

      return await res.json();
    },
    { refetchOnWindowFocus: false, enabled: !!caseId && !!subfactId },
  );
};
